import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { FC, useEffect, useState } from 'react';

import { Highlight } from './Highlights';
import PlayContent from './PlayContent/PlayContent';

type HighlightExpandedViewProps = {
  show: boolean;
  setShow: (state: boolean) => void;
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  highlights: Highlight[];
};

const HighlightExpandedView: FC<HighlightExpandedViewProps> = ({
  show,
  setShow,
  currentIndex,
  setCurrentIndex,
  highlights,
}) => {
  const [translateX, setTranslateX] = useState<string>(`translateX(${-currentIndex * 100}%)`);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);

  const handleTouchStart = (event: React.TouchEvent) => {
    const startX = event.touches[0].clientX;
    let endX = event.touches[0].clientX;

    const handleMove = (e: TouchEvent) => {
      endX = e.touches[0].clientX;
    };

    const handleEnd = () => {
      if (endX - startX > 50 && currentIndex != 0) {
        onChange(currentIndex - 1);
      } else if (startX - endX > 50 && currentIndex < highlights.length - 1) {
        onChange(currentIndex + 1);
      }
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };

    window.addEventListener('touchmove', handleMove);
    window.addEventListener('touchend', handleEnd);
  };

  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
      setTranslateX(`translateX(${-currentIndex * 100}%)`);
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [show, highlights]);

  const onChange = (index: number) => {
    setCurrentIndex(index);
    setIsTransitioning(true);
    setTranslateX(`translateX(${-index * 100}%)`);
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  };

  const onPlayNext = () => {
    if (currentIndex < highlights.length - 1) {
      onChange(currentIndex + 1);
    } else {
      setShow(false);
    }
  };

  return (
    <>
      {show && (
        <div className="fixed w-full h-full flex top-0 left-0 z-[999] bg-black">
          <div className="w-full h-full flex items-center relative justify-center ">
            <XMarkIcon
              className="absolute top-2 right-2 z-[99] h-8 w-8 text-white bg-slate-800 rounded-full cursor-pointer"
              onClick={() => setShow(false)}
            />
            <div
              className="h-[70dvh] md:aspect-[4/5] aspect-auto w-screen md:w-auto flex relative overflow-visible"
              onTouchStart={handleTouchStart}
            >
              <div
                className="w-full h-full flex transition-transform"
                style={{
                  transform: translateX,
                  transitionDuration: isTransitioning ? '300ms' : '0ms',
                }}
              >
                {highlights.map((item, index) => {
                  const brightness = 1 - Math.abs(index - currentIndex) / 5;
                  return (
                    <button
                      key={item.content?._id}
                      style={{
                        scale: currentIndex == index ? '1' : '0.65',
                        filter: `brightness(${brightness})`,
                      }}
                      className="w-full h-full flex flex-shrink-0 relative px-4 md:px-0 transition-all duration-500"
                      onClick={() => {
                        if (currentIndex != index) {
                          setCurrentIndex(index);
                        }
                      }}
                    >
                      <div
                        className="w-full h-full flex"
                        style={{ pointerEvents: currentIndex == index ? 'auto' : 'none' }}
                      >
                        <PlayContent item={item} isActive={currentIndex == index} playNext={onPlayNext} />
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="absolute top-1/2 -left-2 -translate-x-[100%] -translate-y-1/2 ">
                <ChevronRightIcon
                  className={`h-8 w-8 rounded-full rotate-180 ${
                    currentIndex > 0
                      ? 'bg-white text-slate-400 pointer-events-auto'
                      : 'bg-slate-800 text-white pointer-events-none opacity-70'
                  }  shadow-md cursor-pointer hover:scale-110`}
                  onClick={() => {
                    onChange(currentIndex - 1);
                  }}
                />
              </div>

              <div className="absolute top-1/2 -right-2 translate-x-[100%] -translate-y-1/2 ">
                <ChevronRightIcon
                  className={`h-8 w-8 rounded-full ${
                    currentIndex < highlights.length - 1
                      ? 'bg-white text-slate-400 pointer-events-auto'
                      : 'bg-slate-800 text-white pointer-events-none opacity-70'
                  }  shadow-md cursor-pointer hover:scale-110`}
                  onClick={() => {
                    onChange(currentIndex + 1);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HighlightExpandedView;
